export const getCurrencySymbol = (
  inputLocale: string | null | undefined,
  inputCurrency: string | null | undefined
): string => {
  const locale = inputLocale || 'en-US';
  const currency = inputCurrency || 'USD';

  return (
    new Intl.NumberFormat(locale, {
      style: 'currency',
      currencyDisplay: 'narrowSymbol',
      currency,
    })
      .formatToParts()
      .find((part) => part.type === 'currency')?.value || ''
  );
};

export const formatWholeCurrencyInput = (value: string): string => {
  if (!value.trim()) return '';
  const numericValue = Number(value.replace(/[^0-9.-]/g, ''));
  return Number.isFinite(numericValue)
    ? new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(
        numericValue
      )
    : value;
};

export const getBudgetCurrencyOptions = (
  companyCurrency: string,
  currentCurrency: string
): string[] => {
  const currencies =
    typeof Intl.supportedValuesOf === 'function'
      ? Intl.supportedValuesOf('currency')
      : [
          'USD',
          'GBP',
          'EUR',
          'CAD',
          'AUD',
          'JPY',
          'CHF',
          'CNY',
          'INR',
          'NZD',
          'SGD',
          'HKD',
          'NOK',
          'SEK',
          'DKK',
          'ZAR',
        ];
  const defaultCurrency = companyCurrency.trim().toUpperCase();
  return Array.from(
    new Set(
      [defaultCurrency, currentCurrency, ...currencies]
        .filter(Boolean)
        .map((value) => value.trim().toUpperCase())
    )
  ).sort((left, right) =>
    left === defaultCurrency
      ? -1
      : right === defaultCurrency
        ? 1
        : left.localeCompare(right)
  );
};

export const formatCompactCurrency = (
  value: number,
  inputCurrency = 'USD',
  inputLocale = 'en-US'
): string => {
  const safeValue = Number.isFinite(value) ? value : 0;
  const absoluteValue = Math.abs(safeValue);
  const sign = safeValue < 0 ? '-' : '';
  const currencySymbol =
    getCurrencySymbol(inputLocale, inputCurrency) || inputCurrency;

  if (absoluteValue >= 1_000_000) {
    const millions = absoluteValue / 1_000_000;
    const formatted = new Intl.NumberFormat(inputLocale, {
      minimumFractionDigits: millions < 10 ? 1 : 0,
      maximumFractionDigits: 1,
    }).format(millions);

    return `${sign}${currencySymbol}${formatted}M`;
  }

  if (absoluteValue >= 1_000) {
    return `${sign}${currencySymbol}${Math.round(absoluteValue / 1_000)}k`;
  }

  return `${sign}${currencySymbol}${new Intl.NumberFormat(inputLocale, {
    maximumFractionDigits: 0,
  }).format(absoluteValue)}`;
};

const getCurrencyFormatter = (
  locale = 'en-US',
  currency = 'USD',
  amount = 0,
  decimals = 2
) =>
  new Intl.NumberFormat(locale, {
    style: 'currency',
    currencyDisplay: 'narrowSymbol',
    currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount);

export type CurrencyType = {
  locale: string;
  currency: string;
  symbol: string;
  currencyFormatter: (amount: number, decimals: number) => string;
  valueFormatter: (amount: string) => number;
};

export const getCurrencyFromLocale = (
  inputLocale: string | null | undefined,
  inputCurrency: string | null | undefined
): CurrencyType => {
  const locale = inputLocale || 'en-US';
  const currency = inputCurrency || 'USD';

  return {
    locale,
    currency,
    symbol: getCurrencySymbol(locale, currency),
    currencyFormatter: (amount: number, decimals = 2) =>
      getCurrencyFormatter(locale, currency, amount, decimals),
    valueFormatter: (currencyString: string) =>
      Number(currencyString.replace(/[^0-9.-]+/g, '')),
  };
};
